import { coinIcon } from "@/utils/coin-icon";
import style from "./index.module.scss";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "@/store";
import { useSubAccount, useSubAccountsByCurrency } from "@/hooks";
import {
  getSubAccountEarnings$,
  getSubAccountSummary$,
  getSubAccountWorkers$,
  setLoading,
} from "@/store/sub_accounts";
import { useSelector } from "react-redux";
import { Spinner } from "react-bootstrap";
import { useTranslations } from "use-intl";
import { formatHash } from "@/utils/to-hash";
import { Card } from "@/components/Card";
import { EWorkerStatus } from "@/variables";
import {
  CurrencyAggregate,
  HashrateChart,
  IncomeBreakdown,
  PortfolioOverview,
  StatCard,
  WorkerDonut,
} from "@/components";

export default function Statistic() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentSubAccount, loading } = useSubAccount();
  const groups = useSubAccountsByCurrency();
  const statisticsCurrency = useSelector(
    (state: RootState) => state.subAccount.statisticsCurrency,
  );
  const statisticsViewMode = useSelector(
    (state: RootState) => state.subAccount.statisticsViewMode,
  );
  const statisticsSelection = useSelector(
    (state: RootState) => state.subAccount.statisticsSelection,
  );
  const summary = useSelector(
    (state: RootState) => state.subAccount.subAccountSummary,
  );
  const workers = useSelector(
    (state: RootState) => state.subAccount.subAccountWorkers,
  );
  const earnings = useSelector(
    (state: RootState) => state.subAccount.earnings,
  );
  const i18n = useTranslations("cabinet.statistics");

  useEffect(() => {
    if (statisticsViewMode !== "single" || !currentSubAccount) {
      return;
    }
    dispatch(setLoading(true));
    const summaryPromise = dispatch(getSubAccountSummary$(currentSubAccount));
    dispatch(
      getSubAccountWorkers$({
        sub_account_name: currentSubAccount,
        worker_status: EWorkerStatus.ALL,
      }),
    );
    dispatch(getSubAccountEarnings$({ sub_account_name: currentSubAccount }));
    return () => {
      dispatch(setLoading(false));
      summaryPromise.abort();
    };
  }, [currentSubAccount, statisticsViewMode]);

  const fullGroup = groups.find((g) => g.currency === statisticsCurrency);
  // Ticked sub-accounts narrow the currency screen to their sum.
  const currentGroup =
    fullGroup && statisticsSelection.length
      ? {
          ...fullGroup,
          subAccounts: fullGroup.subAccounts.filter((s) =>
            statisticsSelection.includes(s.name),
          ),
        }
      : fullGroup;
  const item = summary?.[0];

  const subtitle =
    statisticsViewMode === "portfolio"
      ? i18n("portfolio.subtitle")
      : statisticsViewMode === "aggregate"
        ? statisticsSelection.length && fullGroup
          ? `${statisticsCurrency} / ${i18n("selectedOf", {
              count: currentGroup?.subAccounts.length ?? 0,
              total: fullGroup.subAccounts.length,
            })}: ${statisticsSelection.join(", ")}`
          : `${statisticsCurrency} / ${i18n("allOf")} ${statisticsCurrency}`
        : `${statisticsCurrency} / ${currentSubAccount}`;

  return (
    <div>
      <p className={style.subtitle}>{subtitle}</p>
      {statisticsViewMode === "portfolio" && <PortfolioOverview />}
      {statisticsViewMode === "aggregate" && currentGroup && (
        <CurrencyAggregate
          group={currentGroup}
          selected={statisticsSelection.length > 0}
        />
      )}
      {statisticsViewMode === "single" && (
        <>
          {loading || !item ? (
            <Spinner animation="grow" />
          ) : (
            <div>
              <div className={style.detailHeader}>
                {coinIcon(item.crypto_currency) && (
                  <img
                  src={coinIcon(item.crypto_currency)}
                  alt={item.crypto_currency}
                  className={style.detailHeader__icon}
                />
                )}
                <h3>{item.crypto_currency}</h3>
              </div>
              <div className={style.statGrid}>
                <Card>
                  <StatCard
                    color="blue"
                    icon="fa-bolt"
                    label={i18n("hashrate10min")}
                    value={formatHash(item.hashrate_10min, item.crypto_currency)}
                  />
                </Card>
                <Card>
                  <StatCard
                    color="teal"
                    icon="fa-chart-line"
                    label={i18n("hashrate1h")}
                    value={formatHash(item.hashrate_1hour, item.crypto_currency)}
                  />
                </Card>
                <Card>
                  <StatCard
                    color="green"
                    icon="fa-users"
                    label={i18n("worker")}
                    value={String(item.active_workers)}
                  />
                </Card>
                <Card>
                  <StatCard
                    color="orange"
                    icon="fa-wallet"
                    label={i18n("balance")}
                    value={`${item.balance} ${item.crypto_currency}`}
                  />
                </Card>
                <Card>
                  <StatCard
                    color="teal"
                    icon="fa-chart-area"
                    label={i18n("hashrate24h")}
                    value={formatHash(item.hashrate_24hour, item.crypto_currency)}
                  />
                </Card>
                <Card>
                  <StatCard
                    color="purple"
                    icon="fa-user-slash"
                    label={i18n("inactive_workers")}
                    value={String(item.unactive_workers)}
                  />
                </Card>
              </div>
              <Card className={style.detailCard}>
                <HashrateChart />
              </Card>
              <div className={style.detailRow}>
                <Card className={style.detailCard}>
                  <h5>{i18n("worker")}</h5>
                  <WorkerDonut workers={workers} />
                </Card>
                <Card className={style.detailCard}>
                  <h5>{i18n("incomeTitle")}</h5>
                  <IncomeBreakdown earnings={earnings} />
                  <button
                    type="button"
                    className="btn btn-link p-0 mt-3"
                    onClick={() => navigate("/cabinet/earnings")}
                  >
                    {i18n("incomeHistoryLink")}
                  </button>
                </Card>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
